// ---------------------------------------------------------------------------
// The `professional` theme (Phase 2 §5) — a polished, web-safe-font, decorated
// theme authored DRY as *deviations* from the `default` base.
//
// AUTHORING FORM ONLY. This `ThemeDefinition` is flattened to a full
// `ResolvedTheme` by `resolveThemeDefinition` (registry.ts) before it is ever
// embedded on a deck. It is frontend-only (never duplicated to the backend or the
// Deno exporter) — the deck stores the flattened resolved snapshot.
//
// Authored as TRUE DELTAS (§4.2, amended 2026-06-07): each placeholder override
// lists only the fields that differ from the base placeholder of the same id;
// omitted fields (font, size, weight, leading, padding, untouched coordinates)
// inherit from `default`. The values here are intentionally simple and meant to be
// redesigned later — their job is to prove the system deviates on EVERY axis:
//   • TITLE              → background + placeholders (geometry & color) + decoration
//   • TITLE_AND_BODY     → placeholders (geometry nudge + title color) + decoration
//   • TITLE_AND_TWO_COLS → decoration only (background + placeholders inherited)
//   • BLANK              → fully inherited (off-white bg, no decoration)
// Decoration uses tokens, sits at zIndex 0 (behind placeholders, zIndex ≥ 1), and
// is shapes only — no image decoration (no asset pipeline).
// ---------------------------------------------------------------------------

import type { ThemeDefinition } from '../registry';

export const PROFESSIONAL_THEME: ThemeDefinition = {
  themeId: 'professional',
  name: 'Professional',
  baseThemeId: 'default',
  palette: {
    background: '#F7F9FC', // subtle cool off-white (≠ default #ffffff)
    surface: '#E8EEF4',
    text: '#1F2A37',
    textMuted: '#5B6B7F',
    primary: '#1F3A5F', // navy
    primaryContrast: '#FFFFFF',
    secondary: '#2E7D6B', // muted teal
    secondaryContrast: '#FFFFFF',
    accent: '#C9A227', // muted gold
  },
  fonts: {
    headingFont: 'Georgia, serif',
    bodyFont: 'Helvetica, sans-serif',
  },
  layouts: {
    // Full-bleed navy title slide: layout-level background override + placeholder
    // geometry/color deltas (white text on navy, centered) + decoration. Title/
    // subtitle inherit the default's font/weight/leading/padding.
    TITLE: {
      background: '$primary',
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 100, y: 190, width: 760, height: 96 },
          textStyle: {
            fontSize: 44,
            color: '$primaryContrast',
            lineHeight: 1,
            textAlign: 'center',
          },
        },
        {
          placeholderId: 'SUBTITLE',
          position: { x: 100, y: 312, width: 760 },
          textStyle: { fontSize: 26, color: '$primaryContrast', textAlign: 'center' },
        },
      ],
      decoration: [
        // Centered gold rule in the band between title (ends y≈286) and subtitle
        // (starts y=312). Horizontally centered: 430..530 around the 480 midline.
        {
          type: 'shape',
          position: { x: 430, y: 298, width: 100, height: 5, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent' },
        },
      ],
    },
    // Off-white background inherited; geometry nudge + navy title color + decoration.
    TITLE_AND_BODY: {
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 90, y: 48, width: 780 },
          textStyle: { color: '$primary' },
        },
        {
          placeholderId: 'BODY',
          position: { x: 90, y: 150, width: 780, height: 340 },
        },
      ],
      decoration: [
        // Top navy bar.
        {
          type: 'shape',
          position: { x: 0, y: 0, width: 960, height: 8, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary' },
        },
        // Gold title underline, tucked just under the title box.
        {
          type: 'shape',
          position: { x: 90, y: 126, width: 160, height: 4, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent' },
        },
      ],
    },
    // Placeholders + background inherited (geometry unchanged) — decoration only.
    TITLE_AND_TWO_COLUMNS: {
      decoration: [
        {
          type: 'shape',
          position: { x: 0, y: 0, width: 960, height: 8, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary' },
        },
        // Thin column divider, centered in the 460..500 gutter between the columns.
        {
          type: 'shape',
          position: { x: 479, y: 150, width: 2, height: 330, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent' },
        },
      ],
    },
    // BLANK inherits the base's empty decoration + (token-resolved) off-white background.
  },
};
